
import React, { useState, useMemo } from 'react';
import { Expense, Category, ExpenseStatus, Recurrence, Group, User } from '../types';
import { Search, Download, Calendar, Repeat, Tag, AlertCircle, CheckCircle2, Users, Copy, Check, Clock, X, ArrowRight, Trash2, Edit2, User as UserIcon, ArrowUpCircle, ArrowDownCircle, Heart } from 'lucide-react';

interface ExpenseListProps {
  expenses: Expense[]; // Already filtered/projected by App
  categories: Category[];
  selectedDate: Date;
  groups: Group[];
  allUsers: User[]; // Needed for Payer Filter
  onToggleStatus?: (expense: Expense) => void;
  onDeleteExpense?: (expense: Expense, deleteScope: 'this' | 'all') => void;
  onEditExpense?: (expense: Expense, updateScope: 'this' | 'all') => void;
  onToggleFavorite?: (expense: Expense) => void;
}

export const ExpenseList: React.FC<ExpenseListProps> = ({ expenses, categories, selectedDate, groups, allUsers, onToggleStatus, onDeleteExpense, onEditExpense, onToggleFavorite }) => {
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterPayer, setFilterPayer] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedGroup, setCopiedGroup] = useState<string | null>(null);

  // Selection State for Detail View (Sheet)
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);

  // Unified Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'delete' | 'edit';
    expense: Expense;
  } | null>(null);

  // --- Filtering Logic ---
  const filteredExpenses = useMemo(() => {
    return expenses.filter(e => {
      const matchesCategory = filterCategory === 'all' || e.categoryId === filterCategory;
      const matchesSearch = e.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPayer = filterPayer === 'all' || e.paidByUserId === filterPayer;
      return matchesCategory && matchesSearch && matchesPayer;
    });
  }, [expenses, filterCategory, searchTerm, filterPayer]);

  // --- Grouping Logic (By Date) ---
  const groupedExpenses = useMemo(() => {
    const groups: Record<string, Expense[]> = {};
    
    filteredExpenses.forEach(expense => {
      const dateObj = new Date(expense.date);
      const dateKey = dateObj.toDateString(); // Groups by full date string
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(expense);
    });

    // Sort dates descending
    return Object.keys(groups)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map(dateKey => ({
        date: new Date(dateKey),
        items: groups[dateKey]
      }));
  }, [filteredExpenses]);


  // --- Helpers ---
  const getCategory = (id: string | null) => categories?.find(c => c.id === id);

  const getStatusInfo = (expense: Expense) => {
    const status = expense.status;
    const expenseDate = new Date(expense.date);
    expenseDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isLate = status === ExpenseStatus.PENDING && expenseDate < today;
    
    if (status === ExpenseStatus.PAID) {
       return { label: 'Pago', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-500/10', icon: <CheckCircle2 size={14} /> };
    }
    if (isLate || status === ExpenseStatus.LATE) {
       return { label: 'Atrasado', color: 'text-red-500 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-500/10', icon: <AlertCircle size={14} /> };
    }
    return { label: 'Pendente', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-500/10', icon: <Clock size={14} /> };
  };

  // --- Actions ---
  const initiateAction = (type: 'edit' | 'delete', expense: Expense) => {
     // 1. Close the detail sheet immediately
     setSelectedExpense(null);
     
     // 2. Open the confirmation modal
     setConfirmModal({
        isOpen: true,
        type,
        expense
     });
  };

  const handleConfirmAction = (scope: 'this' | 'all') => {
     if (!confirmModal) return;
     
     const { type, expense } = confirmModal;

     if (type === 'delete') {
        onDeleteExpense?.(expense, scope);
     } else {
        onEditExpense?.(expense, scope);
     }
     
     setConfirmModal(null);
  };

  const handleToggleStatus = (expense: Expense) => {
    if (onToggleStatus) {
      onToggleStatus(expense);
      // Close modal immediately to give feedback
      setSelectedExpense(null);
    }
  };

  // --- Render ---

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex justify-between items-end px-1">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Extrato</h2>
          <p className="text-slate-400 text-sm mt-1 font-medium capitalize">
            {selectedDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}
          </p>
        </div>
        <button className="p-3 bg-white dark:bg-zinc-900 border border-slate-100 dark:border-zinc-800 shadow-sm rounded-2xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors active:scale-95">
          <Download size={20} />
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col gap-3">
         <div className="relative w-full">
           <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
           <input 
             type="text" 
             placeholder="Buscar..." 
             className="w-full pl-12 pr-4 py-4 bg-white dark:bg-zinc-900 border border-slate-100 dark:border-zinc-800 shadow-sm rounded-2xl text-base font-medium text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-lime transition-all"
             value={searchTerm}
             onChange={(e) => setSearchTerm(e.target.value)}
           />
         </div>
         
         <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {/* Payer Filter */}
            <div className="relative min-w-[160px]">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                    <UserIcon size={16} />
                </div>
                <select 
                  className="w-full appearance-none bg-slate-800 dark:bg-zinc-800 text-white pl-10 pr-8 py-3 rounded-xl text-sm font-bold focus:outline-none cursor-pointer"
                  value={filterPayer}
                  onChange={(e) => setFilterPayer(e.target.value)}
                >
                  <option value="all">Quem pagou: Todos</option>
                  {allUsers?.map(u => <option key={u.id} value={u.id}>{u.name.split(' ')[0]}</option>)}
                </select>
             </div>

            {/* Category Filter */}
            <div className="relative min-w-[140px]">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                    <Tag size={16} />
                </div>
                <select 
                  className="w-full appearance-none bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 text-slate-700 dark:text-slate-300 pl-10 pr-8 py-3 rounded-xl text-sm font-bold focus:outline-none cursor-pointer"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="all">Categoria: Todas</option>
                  {categories?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>
         </div>
      </div>

      {/* List */}
      <div className="space-y-6">
        {groupedExpenses.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-slate-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300 dark:text-slate-600">
               <Calendar size={32} />
            </div>
            <p className="text-slate-500 dark:text-slate-400 font-bold text-lg">Nada por aqui</p>
            <p className="text-slate-400 dark:text-slate-500 text-sm">Nenhuma transação encontrada.</p>
          </div>
        ) : (
          groupedExpenses?.map((group, index) => {
             const isToday = new Date().toDateString() === group.date.toDateString();
             const isYesterday = new Date(new Date().setDate(new Date().getDate() - 1)).toDateString() === group.date.toDateString();
             
             let dateLabel = group.date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
             if (isToday) dateLabel = 'Hoje';
             if (isYesterday) dateLabel = 'Ontem';

             return (
               <div key={index} className="space-y-3">
                 <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest pl-2">{dateLabel}</h3>
                 
                 {group.items.map(expense => {
                   const category = getCategory(expense.categoryId);
                   const isIncome = expense.type === 'income';
                   const statusInfo = getStatusInfo(expense);
                   const isRecurring = expense.recurrence !== Recurrence.NONE || expense.isRecurringInstance;
                   const groupInfo = groups?.find(g => g.id === expense.groupId);
                   
                   return (
                     <div 
                       key={expense.id}
                       onClick={() => setSelectedExpense(expense)}
                       className="bg-white dark:bg-zinc-900 p-4 rounded-3xl shadow-sm border border-slate-100 dark:border-zinc-800 flex items-center gap-4 cursor-pointer active:scale-[0.98] transition-all hover:shadow-md relative group"
                     >
                        {/* Icon */}
                        <div 
                          className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl shrink-0 ${isIncome ? 'bg-brand-lime/20 text-brand-dark' : ''}`}
                          style={{ backgroundColor: !isIncome ? `${category?.color || '#94a3b8'}20` : undefined, color: !isIncome ? category?.color : undefined }}
                        >
                           {isIncome ? <ArrowUpCircle size={24} /> : (category?.icon !== 'Tag' ? category?.name.substring(0,1) : <Tag />)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                           <div className="flex justify-between items-start">
                              <div className="flex items-center gap-2 min-w-0 pr-2">
                                <h4 className="font-bold text-slate-800 dark:text-slate-200 truncate">{expense.description}</h4>
                                <button 
                                   onClick={(e) => {
                                      e.stopPropagation();
                                      onToggleFavorite?.(expense);
                                   }}
                                   className="shrink-0 p-1.5 text-slate-300 hover:text-red-500 dark:text-slate-600 dark:hover:text-red-400 transition-colors rounded-full hover:bg-slate-100 dark:hover:bg-zinc-800"
                                >
                                   <Heart size={16} className={expense.isFavorite ? "fill-red-500 text-red-500" : ""} />
                                </button>
                              </div>
                              <div className="flex flex-col items-end shrink-0">
                                <span className={`font-bold whitespace-nowrap text-base ${isIncome ? 'text-brand-lime text-shadow-sm' : 'text-slate-900 dark:text-white'}`}>
                                   {isIncome ? '+' : '-'} R$ {expense.amount.toFixed(2)}
                                </span>
                              </div>
                           </div>
                           
                           <div className="flex justify-between items-center mt-1">
                              <div className="flex items-center gap-2 text-xs text-slate-400 font-medium">
                                 {groupInfo && (
                                   <span className="flex items-center gap-1 bg-slate-50 dark:bg-zinc-800 px-1.5 py-0.5 rounded-md border border-slate-100 dark:border-zinc-700">
                                      <Users size={10} /> {groupInfo.name}
                                   </span>
                                 )}
                                 {isRecurring && <Repeat size={12} className={expense.isRecurringInstance ? "text-brand-dark dark:text-brand-lime" : ""} />}
                                 <span>{isIncome ? 'Entrada' : category?.name || 'Geral'}</span>
                              </div>

                              {/* Mini Status Dot */}
                              <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusInfo.bg} ${statusInfo.color}`}>
                                 {statusInfo.label}
                              </div>
                           </div>
                        </div>
                     </div>
                   );
                 })}
               </div>
             );
          })
        )}
      </div>

      {/* TRANSACTION DETAILS SHEET (MODAL) */}
      {selectedExpense && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
           {/* Backdrop */}
           <div 
             className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
             onClick={() => setSelectedExpense(null)}
           ></div>

           {/* Sheet Content */}
           <div className="relative w-full max-w-md bg-white dark:bg-zinc-900 rounded-t-[2.5rem] sm:rounded-[2.5rem] p-6 shadow-2xl animate-in slide-in-from-bottom-full duration-300 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                 <div>
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-1">
                      {new Date(selectedExpense.date).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </span>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{selectedExpense.description}</h2>
                 </div>
                 <div className="flex gap-2">
                    <button 
                       onClick={() => onToggleFavorite?.(selectedExpense)}
                       className={`p-2 rounded-full transition-colors ${selectedExpense.isFavorite ? 'bg-red-50 text-red-500 dark:bg-red-500/20' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-slate-400'}`}
                    >
                       <Heart size={20} className={selectedExpense.isFavorite ? "fill-current" : ""} />
                    </button>
                    <button onClick={() => setSelectedExpense(null)} className="p-2 bg-slate-100 dark:bg-zinc-800 rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-zinc-700">
                       <X size={20} />
                    </button>
                 </div>
              </div>

              {/* Big Amount */}
              <div className="text-center py-6 border-b border-slate-100 dark:border-zinc-800 mb-6">
                 <span className={`text-4xl font-bold ${selectedExpense.type === 'income' ? 'text-brand-lime' : 'text-slate-900 dark:text-white'}`}>
                    {selectedExpense.type === 'income' ? '+' : '-'} R$ {selectedExpense.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                 </span>
                 <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase mt-3 ${getStatusInfo(selectedExpense).bg} ${getStatusInfo(selectedExpense).color}`}>
                    {getStatusInfo(selectedExpense).icon}
                    {getStatusInfo(selectedExpense).label}
                 </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                 <div className="bg-slate-50 dark:bg-zinc-800 p-4 rounded-2xl">
                    <div className="text-xs font-bold text-slate-400 uppercase mb-1 flex items-center gap-1"><Tag size={12}/> Categoria</div>
                    <div className="font-bold text-slate-700 dark:text-slate-200">{getCategory(selectedExpense.categoryId)?.name || 'Entrada'}</div>
                 </div>
                 <div className="bg-slate-50 dark:bg-zinc-800 p-4 rounded-2xl">
                    <div className="text-xs font-bold text-slate-400 uppercase mb-1 flex items-center gap-1"><Users size={12}/> Grupo</div>
                    <div className="font-bold text-slate-700 dark:text-slate-200">{groups?.find(g => g.id === selectedExpense.groupId)?.name || 'Pessoal'}</div>
                 </div>
                 <div className="bg-slate-50 dark:bg-zinc-800 p-4 rounded-2xl">
                    <div className="text-xs font-bold text-slate-400 uppercase mb-1 flex items-center gap-1"><Repeat size={12}/> Repetição</div>
                    <div className="font-bold text-slate-700 dark:text-slate-200">{selectedExpense.recurrence}</div>
                 </div>
                 <div className="bg-slate-50 dark:bg-zinc-800 p-4 rounded-2xl">
                    <div className="text-xs font-bold text-slate-400 uppercase mb-1 flex items-center gap-1"><UserIcon size={12}/> Quem pagou</div>
                    <div className="font-bold text-slate-700 dark:text-slate-200">{allUsers?.find(u => u.id === selectedExpense.paidByUserId)?.name.split(' ')[0] || 'Eu'}</div>
                 </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                 <button 
                    onClick={() => handleToggleStatus(selectedExpense)}
                    className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-transform active:scale-[0.98] ${selectedExpense.status === ExpenseStatus.PAID ? 'bg-amber-100 text-amber-600 hover:bg-amber-200 dark:bg-amber-500/20 dark:text-amber-400' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-400'}`}
                 >
                    {selectedExpense.status === ExpenseStatus.PAID ? <Clock size={20} /> : <CheckCircle2 size={20} />}
                    {selectedExpense.status === ExpenseStatus.PAID ? 'Marcar como Pendente' : 'Confirmar Pagamento'}
                 </button>
                 
                 <div className="flex gap-3">
                    <button 
                       onClick={() => initiateAction('edit', selectedExpense)}
                       className="flex-1 py-4 bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-slate-300 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors"
                    >
                       <Edit2 size={18} /> Editar
                    </button>
                    <button 
                       onClick={() => initiateAction('delete', selectedExpense)}
                       className="flex-1 py-4 bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors"
                    >
                       <Trash2 size={18} /> Excluir
                    </button>
                 </div>
              </div>
           </div>
        </div>
      )}

      {/* CONFIRMATION MODAL (Unified) */}
      {confirmModal && (
         <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-zinc-900 w-full max-w-sm rounded-[2rem] p-6 animate-in zoom-in-95 duration-200 shadow-2xl text-center">
               <div className="w-16 h-16 bg-brand-gray dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-500 dark:text-slate-400">
                  {confirmModal.type === 'delete' ? <Trash2 size={32} className="text-red-500" /> : <Edit2 size={32} />}
               </div>
               
               <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">
                  {confirmModal.type === 'delete' ? 'Excluir Transação' : 'Editar Transação'}
               </h3>
               
               <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 leading-relaxed">
                  {confirmModal.type === 'delete' 
                     ? 'Essa ação removerá a transação do seu extrato.'
                     : 'Você será levado para a tela de edição.'}
               </p>

               {/* Recurrence Logic Buttons */}
               {(confirmModal.expense.recurrence !== Recurrence.NONE || confirmModal.expense.isRecurringInstance) ? (
                  <div className="flex flex-col gap-3">
                     <button 
                        onClick={() => handleConfirmAction('this')}
                        className="w-full py-3 bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 rounded-xl font-bold text-slate-700 dark:text-slate-300 transition-colors"
                     >
                        {confirmModal.type === 'delete' ? 'Excluir apenas esta' : 'Editar apenas esta'}
                     </button>
                     <button 
                        onClick={() => handleConfirmAction('all')}
                        className={`w-full py-3 rounded-xl font-bold text-white transition-colors ${confirmModal.type === 'delete' ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-dark hover:bg-slate-800 dark:bg-white dark:text-black'}`}
                     >
                        {confirmModal.type === 'delete' ? 'Excluir todas as futuras' : 'Editar série completa'}
                     </button>
                  </div>
               ) : (
                  <div className="flex gap-3">
                      <button 
                        onClick={() => setConfirmModal(null)}
                        className="flex-1 py-3 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-slate-300 rounded-xl font-bold hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button 
                        onClick={() => handleConfirmAction('all')}
                        className={`flex-1 py-3 rounded-xl font-bold text-white shadow-lg transition-colors ${confirmModal.type === 'delete' ? 'bg-red-500 hover:bg-red-600 shadow-red-200' : 'bg-brand-dark hover:bg-slate-800 dark:bg-white dark:text-black'}`}
                      >
                        {confirmModal.type === 'delete' ? 'Sim, Excluir' : 'Continuar'}
                      </button>
                  </div>
               )}
               
               {/* Close Text for Recurrence only */}
               {(confirmModal.expense.recurrence !== Recurrence.NONE || confirmModal.expense.isRecurringInstance) && (
                  <button 
                     onClick={() => setConfirmModal(null)}
                     className="mt-4 text-sm text-slate-400 font-medium hover:text-slate-600"
                  >
                     Cancelar
                  </button>
               )}
            </div>
         </div>
      )}
    </div>
  );
};